// Interface-name handling: "fa0/1", "f0/1", "FastEthernet 0/1", "gi0/0", "g0/0.10".

const TYPES = [
  ['fastethernet', 'FastEthernet', 'Fa'],
  ['gigabitethernet', 'GigabitEthernet', 'Gi'],
  ['vlan', 'Vlan', 'Vl'],
  ['port-channel', 'Port-channel', 'Po'],
  ['loopback', 'Loopback', 'Lo'],
  ['serial', 'Serial', 'Se'],
  ['tunnel', 'Tunnel', 'Tu'],
];

/** Normalise user input to a canonical interface name that exists on `device`. */
export function parseIfName(input, device) {
  const m = String(input).toLowerCase().replace(/\s+/g, '').match(/^([a-z-]+)(\d+(?:\/\d+)*(?:\.\d+)?)$/);
  if (!m) return null;
  const [, typ, num] = m;
  const cands = TYPES.filter(([full]) => full.startsWith(typ));
  if (cands.length !== 1) return null;
  const name = cands[0][1] + num;
  if (!device) return name;
  return device.interfaces?.[name] ? name : null;
}

export function shortIf(name) {
  for (const [, full, s] of TYPES) if (name.startsWith(full)) return s + name.slice(full.length);
  return name;
}
/** CDP-style abbreviation: "Fas 0/24", "Gig 0/0". */
export function cdpIf(name) {
  for (const [, full] of TYPES) if (name.startsWith(full)) return `${full.slice(0, 3)} ${name.slice(full.length)}`;
  return name;
}

/** Expand "fa0/1 - 4 , fa0/6" into canonical names. Returns null if any part is invalid. */
export function parseIfRange(text, device) {
  const out = [];
  for (const part of text.split(',')) {
    const p = part.trim().replace(/\s*-\s*/, '-');
    const m = p.match(/^([a-z-]+\s*\d+\/)(\d+)(?:-(\d+))?$/i);
    if (!m) return null;
    const [, head, a, b] = m;
    const lo = +a, hi = b ? +b : +a;
    if (hi < lo) return null;
    for (let n = lo; n <= hi; n++) {
      const name = parseIfName(head.replace(/\s+/g, '') + n, device);
      if (!name) return null;
      out.push(name);
    }
  }
  return out;
}

/** Natural sort for interface names. */
export function ifSort(a, b) {
  const na = a.match(/\d+/g).map(Number), nb = b.match(/\d+/g).map(Number);
  if (a.replace(/[\d/.]+/g, '') !== b.replace(/[\d/.]+/g, '')) return a < b ? -1 : 1; // Fa before Gi
  for (let i = 0; i < Math.max(na.length, nb.length); i++) {
    if ((na[i] ?? -1) !== (nb[i] ?? -1)) return (na[i] ?? -1) - (nb[i] ?? -1);
  }
  return 0;
}

/**
 * Interfaces that `interface <name>` creates on demand:
 *   switch: Vlan<1-4094> (SVI), Port-channel<1-48>
 *   router: <physical>.<1-4094> subinterface
 */
export function creatable(name, device) {
  if (!name || !device?.interfaces) return false;
  if (device.kind === 'switch') {
    const v = name.match(/^Vlan(\d+)$/);
    if (v) return +v[1] >= 1 && +v[1] <= 4094;
    const p = name.match(/^Port-channel(\d+)$/);
    if (p) return +p[1] >= 1 && +p[1] <= 48;
    return false;
  }
  if (device.kind === 'router') {
    const lo = name.match(/^Loopback(\d+)$/);
    if (lo) return +lo[1] <= 2147483647;
    const tu = name.match(/^Tunnel(\d+)$/);
    if (tu) return +tu[1] <= 2147483647;
    const m = name.match(/^(.+)\.(\d+)$/);
    return !!m && !!device.interfaces[m[1]] && +m[2] >= 1 && +m[2] <= 4094;
  }
  return false;
}
/** Parse an interface name that may not exist yet (for `interface <name>`). */
export function parseIfNameCreate(input, device) {
  return parseIfName(input, device) || (creatable(parseIfName(input, null), device) ? parseIfName(input, null) : null);
}
